// src/pages/MovieDetails.js
import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
    getMovieDetails,
    getMovieCredits,
    getRecommendedMovies,
    getMovieReviews,
    getSimilarMovies,
} from "../services/movieApi.js";
import { useAuth } from "../context/AuthContext";
import {
    addToWatchlist,
    removeFromWatchlist,
    isMovieInWatchlist,
    addToLikedlist,
    removeFromLikedlist,
    isMovieInLikedlist,
} from "../services/firebase";
import { FaRegHeart, FaHeart, FaRegBookmark, FaBookmark } from "react-icons/fa";
import MovieList from "../components/MovieList.jsx";
import "./MovieDetails.scss";

const MovieDetails = () => {
    const { user } = useAuth();
    const { movieId } = useParams();
    const [movieDetails, setMovieDetails] = useState(null);
    const [movieCredits, setMovieCredits] = useState(null);
    const [recommendedMovies, setRecommendedMovies] = useState(null);
    const [similarMovies, setSimilarMovies] = useState(null);
    const [reviews, setReviews] = useState(null);
    const [isInWatchlist, setIsInWatchlist] = useState(false);
    const [isInLikedlist, setIsInLikedlist] = useState(false);

    const numericMovieId = parseInt(movieId, 10);

    useEffect(() => {
        const fetchMovieDetails = async () => {
            try {
                const data = await getMovieDetails(numericMovieId);
                setMovieDetails(data);
            } catch (error) {
                console.error("Error fetching movie details:", error);
            }
        };

        const fetchMovieCredits = async () => {
            try {
                const data = await getMovieCredits(numericMovieId);
                setMovieCredits(data);
            } catch (error) {
                console.error("Error fetching movie credits:", error);
            }
        };

        const fetchRecommendedMovies = async () => {
            try {
                const data = await getRecommendedMovies(numericMovieId);
                setRecommendedMovies(data);
            } catch (error) {
                console.error("Error fetching recommended movies:", error);
            }
        };

        const fetchSimilarMovies = async () => {
            try {
                const data = await getSimilarMovies(numericMovieId);
                setSimilarMovies(data);
            } catch (error) {
                console.error("Error fetching similar movies:", error);
            }
        };

        const fetchMovieReviews = async () => {
            try {
                const data = await getMovieReviews(numericMovieId);
                setReviews(data);
            } catch (error) {
                console.error("Error fetching reviews:", error);
            }
        };

        // Check if the movie is in the user's watchlist
        const checkWatchlistStatus = async () => {
            if (user) {
                const movieInWatchlist = await isMovieInWatchlist(
                    user.uid,
                    numericMovieId
                );
                setIsInWatchlist(movieInWatchlist);
            }
        };

        // Check if the movie is in the user's likedlist
        const checkLikedlistStatus = async () => {
            if (user) {
                const movieInLikedlist = await isMovieInLikedlist(
                    user.uid,
                    numericMovieId
                );
                setIsInLikedlist(movieInLikedlist);
            }
        };

        checkWatchlistStatus();
        checkLikedlistStatus();
        fetchMovieDetails();
        fetchMovieCredits();
        fetchRecommendedMovies();
        fetchSimilarMovies();
        fetchMovieReviews();
    }, [user, numericMovieId]);

    const handleAddToWatchlist = async () => {
        if (user) {
            await addToWatchlist(user.uid, movieDetails);
            setIsInWatchlist(true);
        }
    };

    const handleRemoveFromWatchlist = async () => {
        if (user) {
            await removeFromWatchlist(user.uid, numericMovieId);
            setIsInWatchlist(false);
        }
    };

    const handleAddToLikedlist = async () => {
        if (user) {
            await addToLikedlist(user.uid, movieDetails);
            setIsInLikedlist(true);
        }
    };

    const handleRemoveFromLikedlist = async () => {
        if (user) {
            await removeFromLikedlist(user.uid, numericMovieId);
            setIsInLikedlist(false);
        }
    };

    if (!movieDetails || !movieCredits || !recommendedMovies || !reviews) {
        return <div>Loading...</div>;
    }

    return (
        <div className="movie_details_container">
            <div className="movie_details layout">
                <img
                    className="movie_banner_image"
                    src={`https://image.tmdb.org/t/p/w1280/${movieDetails.backdrop_path}`}
                    alt={movieDetails.title}
                />
                <div className="movie_title">
                    <h1>{movieDetails.title}</h1>
                    <p className="tagline">{movieDetails.tagline}</p>
                </div>
                <p>{movieDetails.overview}</p>
                <p>Release Date: {movieDetails.release_date}</p>
                <p>Run time: {movieDetails.runtime} min</p>
                <p>Rating: {movieDetails.vote_average}</p>
                <div className="genres_container">
                    <p>Genre: </p>
                    <div className="genres">
                        {movieDetails.genres.map((genre) => (
                            <p key={genre.id}>
                                <Link to={`/genre/${genre.id}`}>
                                    {genre.name}
                                </Link>
                            </p>
                        ))}
                    </div>
                </div>
                <div className="movie_card_icons">
                    {user && (
                        <div className="save_lists">
                            {isInWatchlist ? (
                                <button
                                    title="Remove from Watchlist"
                                    onClick={handleRemoveFromWatchlist}
                                >
                                    <FaBookmark />
                                </button>
                            ) : (
                                <button
                                    title="Add to Watchlist"
                                    onClick={handleAddToWatchlist}
                                >
                                    <FaRegBookmark />
                                </button>
                            )}
                            {isInLikedlist ? (
                                <button
                                    title="Remove from Likedlist"
                                    onClick={handleRemoveFromLikedlist}
                                >
                                    <FaHeart />
                                </button>
                            ) : (
                                <button
                                    title="Add to Likedlist"
                                    onClick={handleAddToLikedlist}
                                >
                                    <FaRegHeart />
                                </button>
                            )}
                        </div>
                    )}
                </div>
                <h2>Cast:</h2>
                <div className="cast_container">
                    {movieCredits.map((cast, index) => (
                        <div key={index} className="cast_details">
                            <img
                                src={`https://image.tmdb.org/t/p/w300/${cast.profile_path}`}
                                alt={cast.naem}
                            />
                            <span>{cast.name}</span>
                        </div>
                    ))}
                </div>
                <h2>Reviews:</h2>
                <div className="reviews_container">
                    {reviews.map((review, index) => (
                        <div className="review_container" key={index}>
                            <p>Author: {review.author}</p>
                            <p>Date: {review.created_at.slice(0, 10)}</p>
                            <p>{review.content}</p>
                        </div>
                    ))}
                </div>
                <h2>Recommended movies:</h2>
                <MovieList movies={recommendedMovies} />
                <h2>Similar movies:</h2>
                <MovieList movies={similarMovies} />
            </div>
        </div>
    );
};

export default MovieDetails;
